/**
 * The last thing that runs when an interactive page — the configurator or the plans
 * page — falls over.
 *
 * Two jobs, and the second is the one that matters during alpha. It shows the
 * shopper something other than a white page — and it captures the error together
 * with the spec this browser holds (in the configurator, the one on screen), which
 * turns a crash into a reproducible native test case rather than a stack trace (see
 * `sentry.ts`).
 *
 * The spec is read from the store rather than passed in, because a boundary that
 * needs props threaded through the component that just crashed is a boundary with a
 * second way to fail.
 */

import { Component, type ErrorInfo, type ReactElement, type ReactNode } from "react";

import { useStore } from "../state/store.js";
import { ReportDesign } from "../ui/ReportDesign.js";
import { captureDesignError } from "./sentry.js";

interface State {
  failed: boolean;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureDesignError(error, useStore.getState().spec);
    console.error("[parametricplans] the page crashed", error, info.componentStack);
  }

  override render(): ReactNode {
    return this.state.failed ? <Fallback /> : this.props.children;
  }
}

function Fallback(): ReactElement {
  const spec = useStore.getState().spec;
  return (
    <div className="placeholder">
      <h2>This page hit a bug</h2>
      <p>
        Reloading usually gets you going again. If you have a moment, send us the
        design — it is what lets us reproduce this exactly.
      </p>
      <ReportDesign spec={spec} context="crash" startOpen />
    </div>
  );
}
